import React from "react";

// CLASSES
import AppConfig from "@classes/AppConfig";
import Validator from "@classes/Validator";

// COMPONENTS
import Button from "@components/Button/Button";
import Form from "@components/Form/Form";
import StackLayout from "@components/layouts/StackLayout/StackLayout";
import TextField from "@components/TextField/TextField";

// HELPERS
import {createClassName, delay} from "@helpers/utils";
import api from "../../../../../helpers/api";
import EditableField from "../../../components/EditableField/EditableField";
import i18n from "../../../../../helpers/i18n";

export default class CobStart extends React.PureComponent {

	state = _getInitialState.call(this);

	render() {
		const {props, state} = this;
		const {app, assentlyStatus} = props;
		const {partner} = app;
		const classes = createClassName("Cob-Start", {});

		let apiUrl = AppConfig.get("apiUrl");
		if (apiUrl === "/") {
			apiUrl = window.location.protocol + '//' + window.location.hostname;
		}

    if (process.env.NODE_ENV === "development") {
      const apiServerPort = AppConfig.get("developmentOptions").apiServerPort;  
      apiUrl = apiServerPort ? apiUrl + ":" + apiServerPort : apiUrl;
    }

		let welcomeParagraph = partner.welcomeParagraph;
		if (welcomeParagraph) {
			welcomeParagraph = welcomeParagraph.replace(new RegExp("#API#", 'g'), apiUrl);
		}

		return (
			<div className={classes}>
				<StackLayout orientation="vertical" fullWidth>
					{partner.welcomeSubtitle && (
						<h2>{partner.welcomeSubtitle}</h2>
					)}

					{welcomeParagraph && (
						<p className={"intro"} dangerouslySetInnerHTML={{__html: welcomeParagraph}}
						   style={{marginBottom: "20px", marginTop: "0"}}/>
					)}

					<Form onSubmit={this._onContinue} submitOnEnter>
						<TextField
							label={"Personnummer (12 siffror)"}
							placeholder="ÅÅÅÅMMDD-XXXX"
							value={state.ssnOrg}
							valid={state.ssnOrg ? state.ssnOrgValid : null}
							onChange={this._onSsnOrgChange}
							onBlur={this._onSsnOrgBlur}
						/>

						<p className={"intro"} style={{marginBottom: "10px"}}>
							Det digitala avtalet kommer att skickas till e-postadressen du anger nedan.
						</p>

						<EditableField
							type="email"
							name="customer_email"
							label={i18n("customer_info", "email")}
							value={state.customer_email}
							valid={state.customer_email_valid ? state.customer_email_valid : null}
							onChange={this._onEmailChange}
							onBlur={this._onEmailBlur}
							required
						/>

						<p className={"intro"} style={{marginBottom: "0"}}>
							Vänligen fyll i övrig information i avtalet i nästa steg
						</p>

						<Button
							type="submit"
							className="continue-button"
							label="Gå vidare"
							appearance={(state.ssnOrg && state.ssnOrgValid && state.customer_email_valid) ? "primary" : "medium"}
							busy={state.busy}
							disabled={!(state.ssnOrg && state.ssnOrgValid && state.customer_email_valid)}
						/>
					</Form>

					{partner.welcomeDisclaimer && (
						<p style={{
							marginTop: "20px",
							marginBottom: "0",
							fontSize: "16px",
							fontStyle: "italic",
							textAlign: "center"
						}}>
							{partner.welcomeDisclaimer}
						</p>
					)}

				</StackLayout>

				{assentlyStatus && assentlyStatus.length > 0 && (
					<div style={{
						margin: "40px 0 0 0",
						background: "#FFF5F5",
						width: "100%",
						display: "block",
						padding: "20px",
						fontSize: "13px"
					}}>
						<strong style={{display: "block", padding: "0 0 5px 0"}}>
							Driftstörning Assently (digital signering)
						</strong>

						För närvarande påverkas följande system: {assentlyStatus.join(", ")} <br/><br/>
						<a href="https://assently.statuspage.io/" target="_blank" rel="noopener noreferrer">
							För mer information, klicka här
						</a>
					</div>
				)}
			</div>
		);
	}

	// Internal methods
	_validateSsnOrg = (value) => {
		const {partner} = this.props.app;
		value = value.replace(/-/g, "");

		if (value.length !== 12) {
			return false;
		}

		const isValidPerson = Validator.validateSsn(value, partner.allowShortSsn);
		const isValidCompany = false;

		if (this.state.personTypeSelected === 'individual') {
			return {
				type: !isValidPerson ? null : "person",
				valid: value ? isValidPerson : null
			};
		}
		else if (this.state.personTypeSelected === 'company') {
			return {
				type: !isValidCompany ? null : "company",
				valid: value ? isValidCompany : null
			};
		}

		if (partner.allowCompany) {
			const isValid = isValidPerson || isValidCompany;
			return {
				type: !isValid ? null : (isValidCompany ? "company" : "person"),
				valid: value ? isValid : null
			};
		}
		else {
			const isValid = isValidPerson;
			return {
				type: !isValid ? null : (isValidPerson ? "person" : "company"),
				valid: value ? isValid : null
			};
		}
	};

	_onEmailChange = (name, value) => {
		const emailValid = Validator.validateEmail(value);

		this.setState({
			customer_email: value,
			customer_email_valid: emailValid
		});
	};

	_onSsnOrgChange = (value) => {
		const ssnOrgValidated = this._validateSsnOrg(value);

		this.setState({
			ssnOrg: value,
			ssnOrgType: ssnOrgValidated.type,
			ssnOrgValid: ssnOrgValidated.valid ? true : null,
		});
	};

	_onSsnOrgBlur = () => this.setState({ssnOrgValid: this._validateSsnOrg(this.state.ssnOrg).valid});
	_onEmailBlur = () => this.setState({customer_email_valid: Validator.validateEmail(this.state.customer_email)});

	_onContinue = () => {
		if (this.state.ssnOrgValid) {
			const {actions} = this.props;

			this.setState({busy: true}, async () => {
				await delay(1000);
				const {props, state} = this;
				const {app} = props;
				const {session, partner} = app;

				try {
					await session.loadLookupData({
						type: partner.allowCompany ? state.ssnOrgType : "person",
						value: state.ssnOrg.replace(/-/g, ""),
					});

					const response = await api("/assently/sendCaseDraft", {
						formFields: {
							customer_social_security_number: state.ssnOrg,
							customer_firstname: session.lookupData["firstName"],
							customer_lastname: session.lookupData["lastName"],
							customer_address_street: session.lookupData["street"],
							customer_address_zipcode: session.lookupData["zipCode"],
							customer_address_city: session.lookupData["city"],
							customer_email: state.customer_email
						}
					});

					if (response.signatories) {
						window.location = response.signatories[0].PartyUrl;
					}
					else {
						this.setState({busy: false});
					}
				}
				catch (e) {
					this.setState({busy: false}, async () => {
						if (e.message === "REQUEST_TIMED_OUT") {
							await actions.openErrorModal({
								title: "Ett fel uppstod",
								body: "Anropet tog för lång tid. Vänligen försök igen.",
							});
						}
						else if (e.message === 'FRAUD_FLAG') {
							await actions.openErrorModal({
								title: "Personnummer spärrat",
								body: "Ditt personnummer är låst via en extern ID-spärr. Kontrollera med ditt " +
									"kreditupplysningsföretag gällande status och för att eventuellt ta bort spärren.",
							});
						}
						else {
							console.log(e.message);
						}
					});
				}
			});
		}
	}
}

// PRIVATE FUNCTIONS
function _getInitialState() {
	const partner = this.props.app.partner;
	return {
		hidden: true,
		busy: false,
		personTypeSelected: partner.allowPersonTypeChoice ? "individual" : "",
		customer_email: "",
		customer_email_valid: false,
		ssnOrg: AppConfig.env === "development" ? "" : "",
		ssnOrgType: AppConfig.env === "development" ? null : null,
		ssnOrgValid: AppConfig.env === "development" ? null : null,
	};
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/Start/simplified/Start.js